import PropTypes, { exact, number, string } from "prop-types";
import { Heading, Separator } from "uikit/components";
import styles from "./RegionSummaryView.module.styl";
import RegionIndicatorCard from "../RegionIndicatorCard/RegionIndicatorCard";
import RegionSummaryCard from "../RegionSummaryCard/RegionSummaryCard";
import { useHistory } from "react-router";
import React, { useContext } from "react";
import { regionIds } from "utils/reports/RegionIds";
import { toDateStringLocale, toShortDateStringLocale } from "utils";
import { MediaContext } from "app/media/MediaContext";
import DigestDocument from "components/DigestDocument/DigestDocument";

export default function RegionSummaryView({
    currentDate,
    previousWeekStartDate,
    previousWeekEndDate,
    currentWeekStartDate,
    currentWeekEndDate,
    regionSummaryData,
    regionRatingModel,
    regionStatsModel,
    regionId,
    digestDocument,
}) {
    const history = useHistory();

    const { isMobile } = useContext(MediaContext);

    const redirectToRegionSchoolsAttendance = function () {
        history.push(`/attendance/region/${regionId}`);
    };

    const redirectToRegionFederationEvents = function () {
        history.push(`/federation-events/region/${regionId}`);
    };

    const redirectToRegionLoading = function () {
        history.push(`/loading/region/${regionId}`);
    };

    const redirectToRegionTrainingPlans = function () {
        history.push(`/training-plans/region/${regionId}`);
    };

    let trainersFiveFK = null;
    let sportsmenFiveFK = null;

    if (regionId === regionIds.MoscowRegionId) {
        trainersFiveFK = { title: "5-ФК (2020 г.)", text: "3713 чел." };
        sportsmenFiveFK = { title: "5-ФК (2020 г.)", text: "107997 чел." };
    }

    return (
        <>
            <div className={styles.headingContainer}>
                <Heading size={"h2"} name="region-summary-header">Отчет по активности региона</Heading>
                {digestDocument && (
                    <DigestDocument
                        link={digestDocument.link}
                        title={digestDocument.title}
                        showAsButton={isMobile}
                        dataTestName="digest_document_button"
                    />
                )}
            </div>
            {isMobile ? null : <Separator margin={"none"} color="grey" />}
            <Heading size={"h2"} className={styles.heading} name="region-overall-header">
                Основные показатели цифровизации
            </Heading>
            <div className={styles.summary}>
                <RegionSummaryCard
                    descriptionTitle="организаций региона в мониторинге"
                    descriptionText="Посмотрите показатели цифровизации ваших организаций"
                    percentage={regionSummaryData.percentOfDigitization}
                    title="Еженедельные данные региона по цифровизации"
                    info='Среднее значение всех показателей цифровизации, выводимых на "Панели управления отраслью спорта".'
                    value={regionSummaryData.organizationsCount}
                    regionId={regionId}
                    regionPositionModel={{
                        regionPosition: regionRatingModel.regionPosition,
                        regionsCount: regionRatingModel.regionsCount,
                        regionRatingText:
                            "место региона по уровню цифровизации спорт. организаций в России на " +
                            toDateStringLocale(currentDate),
                    }}
                    cardName="region-overall-card"
                />
            </div>
            <div className={styles.box}>
                <RegionIndicatorCard
                    dataTestName="region_attendance"
                    descriptionTitle={`выставлено отметок об отсутствии с ${toShortDateStringLocale(
                        previousWeekStartDate,
                    )}
                    по ${toShortDateStringLocale(previousWeekEndDate)}`}
                    numerator={regionSummaryData.trainingSkippedCount}
                    percentage={regionSummaryData.attendancePercent}
                    title="Посещаемость"
                    info="Количество отметок об отсутствии, выставленных за прошлую неделю."
                    onClick={redirectToRegionSchoolsAttendance}
                />
                <RegionIndicatorCard
                    dataTestName="region_object_loading"
                    info={"Загруженность всех спортивных объектов в регионе за прошлую неделю."}
                    descriptionTitle={`средняя загруженность спортивных объектов с ${toShortDateStringLocale(
                        previousWeekStartDate,
                    )} по ${toShortDateStringLocale(previousWeekEndDate)} г.`}
                    numerator={`${regionStatsModel.loadingPercent}%`}
                    percentage={regionStatsModel.loadingPercent}
                    title={"Спортивные объекты"}
                    onClick={redirectToRegionLoading}
                />
            </div>
            <Separator margin={"none"} color={isMobile ? "darkGrey": "grey"} />
            <Heading size={"h2"} className={styles.heading} name="region-statistics-header">
                Статистика по работе с системой
            </Heading>
            <div className={styles.box}>
                <RegionIndicatorCard
                    denominator={regionSummaryData.coachesCount}
                    descriptionTitle={`подтвержденных учетных записей на ${toShortDateStringLocale(
                        currentDate,
                    )}`}
                    descriptionText="Не подтверждено"
                    numerator={regionSummaryData.activeCoachesCount}
                    percentage={regionSummaryData.activeCoachesPercent}
                    title="Тренеры"
                    info="Количество подтвержденных учетных записей тренеров относительно количества созданных учетных записей тренеров."
                    fiveFK={trainersFiveFK}
                    dataTestName="region-coaches"
                />
                <RegionIndicatorCard
                    denominator={regionSummaryData.sportsmansCount}
                    descriptionTitle={`подтвержденных учетных записей на ${toShortDateStringLocale(
                        currentDate,
                    )}`}
                    descriptionText="Не подтверждено"
                    numerator={regionSummaryData.activeSportsmansCount}
                    percentage={regionSummaryData.activeSportsmansPercent}
                    title="Спортсмены"
                    info="Количество подтвержденных учетных записей спортсменов относительно количества созданных учетных записей спортсменов."
                    fiveFK={sportsmenFiveFK}
                    dataTestName="region-sportsmens"
                />
                <RegionIndicatorCard
                    denominator={regionSummaryData.parentsCount}
                    descriptionTitle={`подтвержденных учетных записей на ${toShortDateStringLocale(
                        currentDate,
                    )}`}
                    descriptionText="Не подтверждено"
                    numerator={regionSummaryData.activeParentsCount}
                    percentage={regionSummaryData.activeParentsPercent}
                    title="Родители"
                    info="Количество подтвержденных учетных записей родителей относительно количества созданных учетных записей родителей."
                    dataTestName="region-parents"
                />
                <RegionIndicatorCard
                    denominator={regionSummaryData.organizationsCount}
                    descriptionTitle={`организаций заполнили расписание c ${toShortDateStringLocale(
                        currentWeekStartDate,
                    )}
                    по ${toShortDateStringLocale(currentWeekEndDate)}`}
                    descriptionText="Не заполнили"
                    numerator={regionSummaryData.organizationsWithGoodScheduleCount}
                    percentage={regionSummaryData.goodSchedulePercent}
                    title="Расписание"
                    info='Количество организаций, у которых значение показателя "Расписание в группах (текущая неделя), %" равно 75% и выше, относительно всех организаций в регионе'
                    dataTestName="region-schedule"
                />
                <RegionIndicatorCard
                    denominator={regionSummaryData.organizationsCount}
                    descriptionTitle={`организаций заполнили журнал на 75% и более c ${toShortDateStringLocale(
                        previousWeekStartDate,
                    )}
                    по ${toShortDateStringLocale(previousWeekEndDate)}`}
                    descriptionText="Не заполнили"
                    numerator={regionSummaryData.organizationsWithoutGoodJournalCount}
                    percentage={regionSummaryData.goodJournalPercent}
                    title="Журнал"
                    info='Количество организаций, у которых значение показателя "Журнал" равно 75% и выше, относительно всех организаций в регионе.'
                    dataTestName="region-journal"
                />
                <RegionIndicatorCard
                    dataTestName="region_training_plans"
                    denominator={regionSummaryData.trainingsCount}
                    descriptionTitle={`тренировок с упражнениями с ${toShortDateStringLocale(
                        previousWeekStartDate,
                    )}
                    по ${toShortDateStringLocale(previousWeekEndDate)}`}
                    numerator={regionSummaryData.trainingsWithFilledPlanCount}
                    percentage={regionSummaryData.trainingsWithFilledPlanPercentage}
                    title="Ведение планов тренировок"
                    info={
                        "Количество тренировок с упражнениями относительно общего количества тренировок."
                    }
                    onClick={redirectToRegionTrainingPlans}
                />
            </div>
            <Separator margin={"none"} color={isMobile ? "darkGrey": "grey"} />
            <Heading size={"h2"} className={styles.heading} name="region-events-header">
                Реализация календарного плана физкультурных и спортивных мероприятий
            </Heading>
            <div className={styles.box}>
                <RegionIndicatorCard
                    dataTestName="region_events"
                    denominator={regionStatsModel.plannedEventsCount}
                    descriptionTitle={`мероприятий проведено из КП текущего года`}
                    numerator={regionStatsModel.conductedEventsCount}
                    title="Количество проведенных мероприятий из КП"
                    info="Количество проведенных мероприятий из календарного плана (КП) региона в текущем году."
                    onClick={redirectToRegionFederationEvents}
                />
                <RegionIndicatorCard
                    dataTestName="region_events_with_documents"
                    denominator={regionStatsModel.conductedEventsCount}
                    descriptionTitle={`мероприятий с результатами из КП текущего года`}
                    numerator={regionStatsModel.eventsWithDocuments}
                    percentage={regionStatsModel.eventsWithDocumentsToEventsConductedPercentage}
                    title="Наличие результатов по мероприятиям"
                    info="Количество проведенных мероприятий из календарного плана (КП), по которым были загружены файлы с результатами."
                    onClick={redirectToRegionFederationEvents}
                />
            </div>
        </>
    );
}

RegionSummaryView.propTypes = {
    currentDate: string.isRequired,
    previousWeekStartDate: string.isRequired,
    previousWeekEndDate: string.isRequired,
    currentWeekStartDate: string.isRequired,
    currentWeekEndDate: string.isRequired,
    regionId: number.isRequired,
    regionSummaryData: exact({
        organizationsCount: number.isRequired,
        coachesCount: number.isRequired,
        activeCoachesCount: number.isRequired,
        activeCoachesPercent: number.isRequired,
        sportsmansCount: number.isRequired,
        activeSportsmansCount: number.isRequired,
        activeSportsmansPercent: number.isRequired,
        parentsCount: number.isRequired,
        activeParentsCount: number.isRequired,
        activeParentsPercent: number.isRequired,
        organizationsWithGoodScheduleCount: number.isRequired,
        goodSchedulePercent: number.isRequired,
        organizationsWithoutGoodJournalCount: number.isRequired,
        goodJournalPercent: number.isRequired,
        percentOfDigitization: number.isRequired,
        trainingSkippedCount: number.isRequired,
        attendancePercent: number.isRequired,
        trainingsCount: number.isRequired,
        trainingsWithFilledPlanCount: number.isRequired,
        trainingsWithFilledPlanPercentage: number.isRequired,
    }).isRequired,
    regionRatingModel: exact({
        regionPosition: number.isRequired,
        regionsCount: number.isRequired,
    }),
    regionStatsModel: exact({
        loadingPercent: number.isRequired,
        plannedEventsCount: number.isRequired,
        conductedEventsCount: number.isRequired,
        eventsWithDocuments: number.isRequired,
        eventsWithDocumentsToEventsConductedPercentage: number.isRequired
    }),
    digestDocument: exact({
        id: string.isRequired,
        title: string.isRequired,
        link: string.isRequired,
        showAsButton: PropTypes.bool.isRequired,
    }),
};
